import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';

import { Button, Input, Textarea, Tag, Badge, Card, Icon } from '@lib/ui';
import { Modal, AnimatedTick } from '@lib/ui';
import { ChatBubble } from '../components';
import type { WizardPersona } from '../types';
import {
  defaultPersona,
  defaultWorldview,
  defaultVoice,
  defaultDecisions,
  generateMockContent,
} from '../types';
import {
  INTERVIEW_QUESTIONS,
  formalitySelectToValue,
  confidenceSelectToValue,
  callToActionSelectToValue,
} from './questions';

interface Message {
  id: string;
  type: 'assistant' | 'user';
  content: string;
  questionId?: string;
  isHelp?: boolean;
  isPreview?: boolean;
  previewData?: { content: string; highlights: string[] };
}

export type InterviewWizardProps = {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: (persona: WizardPersona) => void;
};

export function InterviewWizard({ isOpen, onClose, onComplete }: InterviewWizardProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [persona, setPersona] = useState<Partial<WizardPersona>>(defaultPersona);
  const [showHelp, setShowHelp] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showPreviousAnswers, setShowPreviousAnswers] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentQuestion =
    currentQuestionIndex < INTERVIEW_QUESTIONS.length
      ? INTERVIEW_QUESTIONS[currentQuestionIndex]
      : null;

  // Initialize first message
  useEffect(() => {
    if (isOpen && messages.length === 0 && INTERVIEW_QUESTIONS.length > 0) {
      const firstQuestion = INTERVIEW_QUESTIONS[0];
      setMessages([
        {
          id: 'q-0',
          type: 'assistant',
          content: firstQuestion.question,
          questionId: firstQuestion.id,
        },
      ]);
    }
  }, [isOpen, messages.length]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when question changes
  useEffect(() => {
    if (currentQuestion?.type === 'text' || currentQuestion?.type === 'textarea') {
      inputRef.current?.focus();
    }
  }, [currentQuestion]);

  const updatePersona = (field: string, value: unknown) => {
    if (field === '_test') return;

    const parts = field.split('.');

    if (parts.length === 2) {
      const [parent, child] = parts;

      if (parent === 'worldview') {
        setPersona((prev) => ({
          ...prev,
          worldview: {
            ...defaultWorldview,
            ...prev.worldview,
            [child]: value,
          },
        }));
      } else if (parent === 'voice') {
        setPersona((prev) => ({
          ...prev,
          voice: {
            ...defaultVoice,
            ...prev.voice,
            [child]: value,
          },
        }));
      } else if (parent === 'decisions') {
        setPersona((prev) => ({
          ...prev,
          decisions: {
            ...defaultDecisions,
            ...prev.decisions,
            [child]: value,
          },
        }));
      }
    } else {
      setPersona((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = (answer: string | string[]) => {
    if (!currentQuestion) return;

    const answerText = Array.isArray(answer) ? answer.join(', ') : answer;

    // Add user message
    setMessages((prev) => [
      ...prev,
      {
        id: `a-${currentQuestionIndex}`,
        type: 'user',
        content: answerText,
      },
    ]);

    // Convert and update persona based on field type
    if (currentQuestion.field === 'decisions.formality' && typeof answer === 'string') {
      updatePersona(currentQuestion.field, formalitySelectToValue(answer));
    } else if (currentQuestion.field === 'decisions.confidence' && typeof answer === 'string') {
      updatePersona(currentQuestion.field, confidenceSelectToValue(answer));
    } else if (currentQuestion.field === 'decisions.callToAction' && typeof answer === 'string') {
      updatePersona(currentQuestion.field, callToActionSelectToValue(answer));
    } else {
      updatePersona(currentQuestion.field, answer);
    }

    // Clear input
    setInputValue('');
    setSelectedOptions([]);
    setShowHelp(false);

    // Check for follow-up
    const followUp = currentQuestion.followUp?.(answerText);

    // Move to next question or complete
    if (currentQuestionIndex < INTERVIEW_QUESTIONS.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      const nextQuestion = INTERVIEW_QUESTIONS[nextIndex];

      setTimeout(() => {
        // Add follow-up if exists
        if (followUp && currentQuestion.field === '_test') {
          // Generate preview for test question
          const { content, highlights } = generateMockContent({
            persona,
            channel: 'LinkedIn',
            topic: answerText,
          });

          setMessages((prev) => [
            ...prev,
            {
              id: `followup-${currentQuestionIndex}`,
              type: 'assistant',
              content: followUp,
              isPreview: true,
              previewData: { content, highlights },
            },
          ]);
        }

        // Add next question
        setMessages((prev) => [
          ...prev,
          {
            id: `q-${nextIndex}`,
            type: 'assistant',
            content: nextQuestion.question,
            questionId: nextQuestion.id,
          },
        ]);

        setCurrentQuestionIndex(nextIndex);
      }, 500);
    } else {
      // Complete
      setTimeout(() => {
        setIsCompleted(true);
        setTimeout(() => {
          onComplete?.(persona as WizardPersona);
        }, 2000);
      }, 500);
    }
  };

  const handleSkip = () => {
    if (!currentQuestion || currentQuestion.required) return;
    handleSubmit('(skipped)');
  };

  const handleShowHelp = () => {
    if (!currentQuestion?.examples) return;

    setShowHelp(true);
    setMessages((prev) => [
      ...prev,
      {
        id: `help-${currentQuestionIndex}`,
        type: 'assistant',
        content: `Here are some examples:\n\n${currentQuestion.examples?.map((e) => `• ${e}`).join('\n') || ''}`,
        isHelp: true,
      },
    ]);
  };

  const toggleOption = (option: string) => {
    setSelectedOptions((prev) =>
      prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
    );
  };

  const renderInputArea = () => {
    if (isCompleted) return null;
    if (!currentQuestion) return null;

    switch (currentQuestion.type) {
      case 'text':
        return (
          <div className="flex items-center gap-2">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your answer..."
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && inputValue.trim() && handleSubmit(inputValue)}
            />
            <Button
              onClick={() => handleSubmit(inputValue)}
              disabled={!inputValue.trim() && currentQuestion.required}
            >
              <Icon name="send" size="md" />
            </Button>
          </div>
        );

      case 'textarea':
        return (
          <div className="space-y-2">
            <Textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your answer..."
              rows={3}
            />
            <div className="flex justify-end">
              <Button
                onClick={() => handleSubmit(inputValue)}
                disabled={!inputValue.trim() && currentQuestion.required}
              >
                <Icon name="send" size="md" />
                <span className="ml-1">Send</span>
              </Button>
            </div>
          </div>
        );

      case 'select':
        return (
          <div className="flex flex-wrap gap-2">
            {currentQuestion.options?.map((option) => (
              <Button
                key={option}
                variant={inputValue === option ? 'filled' : 'outlined'}
                onClick={() => {
                  setInputValue(option);
                  handleSubmit(option);
                }}
              >
                {option}
              </Button>
            ))}
          </div>
        );

      case 'multiselect':
        return (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {currentQuestion.options?.map((option) => (
                <Tag
                  key={option}
                  variant={selectedOptions.includes(option) ? 'filled' : 'outlined'}
                  color={selectedOptions.includes(option) ? 'primary' : 'neutral'}
                  className="cursor-pointer"
                  onClick={() => toggleOption(option)}
                >
                  {option}
                </Tag>
              ))}
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => handleSubmit(selectedOptions)}
                disabled={selectedOptions.length === 0 && currentQuestion.required}
              >
                <Icon name="send" size="md" />
                <span className="ml-1">Continue</span>
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Collect previous answers for sidebar
  const previousAnswers = messages.filter((m) => m.type === 'user');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Persona Interview"
      size="lg"
      showCloseButton
    >
      <div className="flex h-[60vh] min-h-[400px]">
        {/* Previous answers sidebar (collapsible) */}
        {previousAnswers.length > 0 && (
          <div
            className={clsx(
              'border-r border-gray-200 dark:border-dark-600 transition-all duration-200',
              showPreviousAnswers ? 'w-48 p-3' : 'w-10'
            )}
          >
            <button
              type="button"
              onClick={() => setShowPreviousAnswers(!showPreviousAnswers)}
              className="w-full text-left text-xs font-medium text-gray-500 dark:text-dark-400 mb-2 hover:text-gray-700 dark:hover:text-dark-200"
            >
              {showPreviousAnswers ? 'Hide' : '...'}
            </button>
            {showPreviousAnswers && (
              <div className="space-y-2 text-xs">
                {previousAnswers.slice(0, -1).map((msg, idx) => (
                  <div key={msg.id} className="truncate text-gray-600 dark:text-dark-300">
                    <span className="font-medium">{idx + 1}.</span> {msg.content}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Main chat area */}
        <div className="flex-1 flex flex-col">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div key={message.id}>
                <ChatBubble variant={message.type} animate>
                  {message.isPreview && message.previewData ? (
                    <div className="space-y-2">
                      <p>{message.content}</p>
                      <Card skin="bordered" className="p-3 mt-2 bg-gray-50 dark:bg-dark-700">
                        <pre className="whitespace-pre-wrap text-xs text-gray-600 dark:text-dark-300">
                          {message.previewData.content}
                        </pre>
                        {message.previewData.highlights.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {message.previewData.highlights.map((h, i) => (
                              <Badge key={i} variant="soft" color="success" className="text-xs">
                                {h}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </Card>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap">{message.content}</div>
                  )}
                </ChatBubble>

                {/* Help text for current question */}
                {message.questionId === currentQuestion?.id && currentQuestion?.helpText && (
                  <p className="mt-1 ml-11 text-xs text-gray-500 dark:text-dark-400">
                    {currentQuestion.helpText}
                  </p>
                )}
              </div>
            ))}

            {/* Completion message */}
            {isCompleted && (
              <div className="flex flex-col items-center justify-center py-8">
                <AnimatedTick color="success" size="xl" className="mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Persona Created!
                </h3>
                <p className="text-sm text-gray-500 dark:text-dark-400 mt-1">
                  "{persona.name}" is ready to go
                </p>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          {!isCompleted && (
            <div className="border-t border-gray-200 dark:border-dark-600 p-4">
              {/* Action buttons */}
              {currentQuestion && (
                <div className="flex items-center gap-2 mb-3">
                  {currentQuestion.examples && !showHelp && (
                    <Button variant="flat" className="text-xs px-2 py-1" onClick={handleShowHelp}>
                      <Icon name="help" size="md" />
                      <span className="ml-1">Help me answer</span>
                    </Button>
                  )}
                  {!currentQuestion.required && (
                    <Button variant="flat" className="text-xs px-2 py-1" onClick={handleSkip}>
                      <Icon name="skip" size="md" />
                      <span className="ml-1">Skip for now</span>
                    </Button>
                  )}
                </div>
              )}

              {renderInputArea()}

              {/* Progress */}
              <div className="mt-3 flex items-center justify-between text-xs text-gray-500 dark:text-dark-400">
                <span>
                  Question {currentQuestionIndex + 1} of {INTERVIEW_QUESTIONS.length}
                </span>
                <div className="flex gap-1">
                  {INTERVIEW_QUESTIONS.map((_, idx) => (
                    <div
                      key={idx}
                      className={clsx(
                        'size-1.5 rounded-full',
                        idx <= currentQuestionIndex
                          ? 'bg-primary-500'
                          : 'bg-gray-300 dark:bg-dark-600'
                      )}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default InterviewWizard;
